import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Params } from '@angular/router';
import { APP_CONFIG, IAppConfig } from '@tcc-mono/shared/app-config';
import { BaseService, PaginatedResponse, Response } from '@tcc-mono/shared/data-access';
import { downloadToDisk } from '@tcc-mono/shared/utils';
import {
  Animal,
  TransportGroup,
  TransportGroupDayCheckParams,
  TransportGroupDayCheckResult,
  TransportGroupRvoReportResult,
  GroupedTransportLists
} from '@tcc-mono/vandijk/shared/data-access/models';
import dayjs from 'dayjs';
import { Observable, map, catchError, throwError } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class TransportGroupService extends BaseService<TransportGroup> {
  public constructor(
    _http: HttpClient,
    @Inject(APP_CONFIG) appConfig: IAppConfig
  ) {
    super(_http, appConfig);
    this.path = 'api/transport_groups';
  }

  public getAnimals = (transportGroupId: string | number, params?: Params) => {
    return this.getChild(transportGroupId, 'animals', params) as Observable<
      PaginatedResponse<Animal>
    >;
  };

  public deleteAnimal = (transportGroupId: string | number, animalId: string | number): Observable<Animal> => {
    return this.delete(transportGroupId, `animals/${animalId}`);
  };

  public close = (transportGroupId: number) => {
    return this.postChild({}, `${transportGroupId}/close`);
  };

  public brightExport = (transportGroupId: number) => {
    return this.postChild({}, `${transportGroupId}/bright_export`);
  };

  public brightExportBulk = (transportGroupIds: number[]) => {
    return this._http.post(this.getUrl(`${this.path}/bright_export`),
      { transport_group_ids: transportGroupIds }
    );
  };

  public brightDownload = (id: number): Observable<unknown> => {
    //TODO: Get correct filename
    const filename = `BS${dayjs().format('YYYYMMDD-HHmmss')}-trs-${id}-VeehandelCorVanDijkbv.xlsx`;

    return this._http.get(this.getUrl(`${this.path}/${id}/bright_export`), { responseType: 'blob' })
    .pipe(
      downloadToDisk(filename)
    );
  };

          public denkavitExport = (transportGroupIds: number[]): Observable<Blob> => {
    return this._http.post(this.getUrl(`${this.path}/denkavit_export`),
      { transport_group_ids: transportGroupIds },
      { responseType: 'text' }
    ).pipe(
      map((textContent: string): Blob => {
        // Convert text content to blob for download
        return new Blob([textContent], { type: 'text/plain' });
      })
    );
  };

  public brightExportBuyer = (transportGroupIds: number[]): Observable<Blob> => {
    return this._http.post(this.getUrl(`${this.path}/bright_export_buyer`),
      { transport_group_ids: transportGroupIds },
      { responseType: 'blob' }
    );
  };

  public animalDetailsExport = (transportGroupIds: number[]): Observable<Blob> => {
    return this._http.post(this.getUrl(`${this.path}/animal_details_export`),
      { transport_group_ids: transportGroupIds },
      { responseType: 'blob' }
    );
  };

  public brightExportBuyerMail = (transportGroupIds: number[]) => {
    return this._http.post(this.getUrl(`${this.path}/bright_export_buyer_mail`),
      { transport_group_ids: transportGroupIds }
    );
  };

  public brightExportBuyerApi = (transportGroupIds: number[]) => {
    return this._http.post(this.getUrl(`${this.path}/bright_export_buyer_api`),
      { transport_group_ids: transportGroupIds }
    );
  };

  public denkavitExportMail = (transportGroupIds: number[]) => {
    return this._http.post(this.getUrl(`${this.path}/denkavit_export_mail`),
      { transport_group_ids: transportGroupIds }
    );
  };

  public reportRvo = (transportGroupId: number, test: boolean = true): Observable<Response<TransportGroupRvoReportResult[]>> => {
    return this.post(
      null,
      `/${transportGroupId}/rvo/transport_animals${test ? `/test` : ``}`
    );
  };

  getTransportGroupExcel = (transportGroupId: number) => {
    return this._http.get(
      `${this.getUrl(
        `${this.path}/${transportGroupId}/export_animals`
      )}`,
      {
        responseType: 'blob'
      }
    );
  };

  getGroupedTransportLists = (dateFrom: string, dateTo: string, vehicleId: number | null = null): Observable<GroupedTransportLists[]> => {
    const params: Params = {
      date_from: dateFrom,
      date_to: dateTo,
    };

    if (vehicleId) {
      params['vehicle_id'] = vehicleId;
    }

    return this._http.get<GroupedTransportLists[]>(this.getUrl(`${this.path}/get_transports_for_vehicle_by_date_and_destination`), {
      params
    });
  };

  createTransport = (transport_group_ids: number[], showWeights?: boolean, override?: boolean): Observable<Blob> => {
    console.log('transport_group_ids', transport_group_ids);
    const params: any = {};
    if (override) {
      params.override = 'true';
    }

    return this._http.post(this.getUrl(`${this.path}/create_transport`),
      {
        transport_group_ids,
        show_weight: showWeights
      },
      {
        responseType: 'blob',
        params: params
      }
    );
  };

  downloadTransportPdf = (transportId: number): Observable<Blob> => {
    return this._http.post(this.getUrl(`${this.path}/download_transport_pdf`),
      { transport_id: transportId },
      { responseType: 'blob' }
    );
  };

  public dayCheck = (params: TransportGroupDayCheckParams): Observable<TransportGroupDayCheckResult> => {
    return this._http.post<TransportGroupDayCheckResult>(
      this.getUrl(`${this.path}/day_check`),
      {
        date: params.date,
        groups: params.groups
      }
    );
  };
}
